import { supabase } from './supabaseClient.ts';
import type { StartScanRequest, StartScanResponse } from '../types/scan.ts';
import type { SubmitFeedbackRequest, SubmitFeedbackResponse } from '../types/feedback.ts';
import { isLocalhost } from '../utils/isLocalhost.ts';
import { withSupabaseAvailability } from '../utils/supabaseStatus.ts';
// Session type not currently used in this file

// Always route to Supabase Cloud Edge Functions (cloud-only mode)
const getEdgeFunctionUrl = (functionName: string) => {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  if (!supabaseUrl) {
    throw new Error('VITE_SUPABASE_URL not configured');
  }
  const url = `${supabaseUrl}/functions/v1/${functionName}`;
  if (import.meta.env.DEV) console.log(`[apiService] Calling: ${url}`);
  return url;
};

export async function startScan(payload: { sourceType: 'upload' | 'github'; fileUrl?: string }) {
  return await withSupabaseAvailability(async () => {
    const isLocal = typeof window !== 'undefined' && window.location.hostname === 'localhost';
    if (import.meta.env.DEV) console.log('[apiService.startScan] Environment:', isLocal ? 'LOCAL' : 'PRODUCTION');
    if (import.meta.env.DEV) console.log('[apiService.startScan] invoked with:', payload);
    
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error || !session) {
      console.error('[apiService.startScan] No active session:', error);
      throw new Error('No session');
    }
    if (import.meta.env.DEV) console.log('[apiService.startScan] session.user.id:', session.user.id);
    if (import.meta.env.DEV) console.log('[apiService.startScan] session.access_token present:', !!session.access_token);
    if (import.meta.env.DEV) console.log('[apiService.startScan] Using access_token:', session.access_token.slice(0, 10), '...');

    const body: StartScanRequest = {
      session_id: session.user.id,
      source_type: payload.sourceType,
      file_url: payload.fileUrl,
    };

    if (import.meta.env.DEV) console.log('[apiService.startScan] constructed request:', body);
    
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
    };
    if (import.meta.env.DEV) console.log('[apiService.startScan] request headers:', {
      'Content-Type': headers['Content-Type'],
      'Authorization': `Bearer ${session.access_token.slice(0, 10)}...`,
    });

    // FIXED: Use absolute Supabase Cloud URL instead of relative path
    const functionUrl = getEdgeFunctionUrl('start-scan');
    if (import.meta.env.DEV) console.log('[apiService.startScan] Calling Edge Function:', functionUrl);

    let res: Response;
    try {
      res = await fetch(functionUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });
    } catch (networkError) {
      console.error('[apiService.startScan] Network/CORS error:', networkError);
      if (isLocalhost()) {
        window.alert('Local development: Edge Function unavailable or blocked by CORS. Is Supabase CLI running?');
      }
      throw networkError;
    }

    if (import.meta.env.DEV) console.log('[apiService.startScan] response status:', res.status);
    if (import.meta.env.DEV) console.log('[apiService.startScan] response ok:', res.ok);

    if (!res.ok) {
      // Clone the response to avoid 'body already read' error
      const errorText = await res.clone().text();
      console.error('[apiService.startScan] Server error:', errorText);
      throw new Error(`Request failed (${res.status}): ${errorText}`);
    }

    const data = await res.json();
    if (import.meta.env.DEV) console.log('[apiService.startScan] Response data:', data);
    return data as StartScanResponse;
  });
}

export async function uploadZipFile(file: File) {
  return await withSupabaseAvailability(async () => {
    if (import.meta.env.DEV) console.log('[apiService.uploadZipFile] invoked with file:', file.name, file.size);
    
    // Get session and refresh if needed
    console.log('[apiService.uploadZipFile] Getting session...');
    let { data: { session }, error } = await supabase.auth.getSession();
    if (error || !session) {
      console.error('[apiService.uploadZipFile] No active session:', error);
      throw new Error('No active session');
    }
    
    // Check if token is close to expiration and refresh if needed
    const now = Math.floor(Date.now() / 1000);
    const expiresAt = session.expires_at || 0;
    const timeUntilExpiry = expiresAt - now;
    
    console.log('[apiService.uploadZipFile] Token expiry check:', {
      now,
      expiresAt,
      timeUntilExpiry,
      expiresInMinutes: Math.floor(timeUntilExpiry / 60)
    });
    
    // If token expires within 5 minutes, refresh it
    if (timeUntilExpiry < 300) {
      console.log('[apiService.uploadZipFile] Token expiring soon, refreshing...');
      const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
      if (refreshError) {
        console.error('[apiService.uploadZipFile] Session refresh failed:', refreshError);
        throw new Error('Session refresh failed');
      }
      session = refreshData.session;
      console.log('[apiService.uploadZipFile] Session refreshed successfully');
    }
    
    if (!session) {
      console.error('[apiService.uploadZipFile] No session after refresh attempt');
      throw new Error('No active session after refresh');
    }
    
    console.log('[apiService.uploadZipFile] session.access_token present:', !!session.access_token);
    console.log('[apiService.uploadZipFile] session.access_token length:', session.access_token?.length);
    console.log('[apiService.uploadZipFile] session.access_token preview:', session.access_token?.slice(0, 20) + '...');

    const url = getEdgeFunctionUrl('upload-zip');
    console.log('[apiService.uploadZipFile] Calling Edge Function:', url);

    console.log('[apiService.uploadZipFile] 🔍 DETAILED FILE ANALYSIS BEFORE UPLOAD:');
    console.log('[apiService.uploadZipFile] File object type:', typeof file);
    console.log('[apiService.uploadZipFile] File constructor:', file.constructor.name);
    console.log('[apiService.uploadZipFile] File properties:');
    console.log('[apiService.uploadZipFile] - name:', file.name);
    console.log('[apiService.uploadZipFile] - size:', file.size, 'bytes');
    console.log('[apiService.uploadZipFile] - type:', file.type);
    console.log('[apiService.uploadZipFile] - lastModified:', file.lastModified);
    console.log('[apiService.uploadZipFile] File size in MB:', (file.size / 1000000).toFixed(2), 'MB');

    const formData = new FormData();
    formData.append('file', file);
    
    console.log('[apiService.uploadZipFile] 🔍 FORMDATA ANALYSIS:');
    console.log('[apiService.uploadZipFile] FormData created successfully');
    console.log('[apiService.uploadZipFile] FormData has file field:', formData.has('file'));
    const formDataFile = formData.get('file') as File;
    if (formDataFile) {
      console.log('[apiService.uploadZipFile] FormData file size:', formDataFile.size, 'bytes');
      console.log('[apiService.uploadZipFile] FormData file name:', formDataFile.name);
      console.log('[apiService.uploadZipFile] Size match check:', file.size === formDataFile.size);
    } else {
      console.error('[apiService.uploadZipFile] ❌ No file found in FormData!');
    }

    // Make the request with retry logic for 401 errors
    const makeRequest = async (token: string) => {
      return await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });
    };

    let res: Response;
    try {
      // First attempt with current token
      res = await makeRequest(session.access_token);
      
      // If we get a 401, try refreshing the session once
      if (res.status === 401) {
        console.log('[apiService.uploadZipFile] Got 401, attempting to refresh session...');
        
        const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
        if (refreshError || !refreshData.session?.access_token) {
          console.error('[apiService.uploadZipFile] Session refresh failed:', refreshError);
          throw new Error('Authentication required - please login again');
        }
        
        console.log('[apiService.uploadZipFile] Session refreshed, retrying request...');
        res = await makeRequest(refreshData.session.access_token);
      }
    } catch (networkError) {
      console.error('[apiService.uploadZipFile] Network error:', networkError);
      throw networkError; // This will be caught by withSupabaseAvailability
    }

    console.log('[apiService.uploadZipFile] response status:', res.status);
    console.log('[apiService.uploadZipFile] response ok:', res.ok);

    if (!res.ok) {
      const errorText = await res.clone().text();
      console.error('[apiService.uploadZipFile] Server error:', errorText);
      
      // Special handling for 401 after retry
      if (res.status === 401) {
        throw new Error('Authentication failed - please login again');
      }
      
      throw new Error(`Request failed (${res.status}): ${errorText}`);
    }

    let data;
    try {
      data = await res.json();
    } catch (parseError) {
      console.error('[apiService.uploadZipFile] Failed to parse JSON response:', parseError);
      throw new Error('Invalid response from server. Please try again.');
    }

    if (!data) {
      console.warn('[apiService.uploadZipFile] Response was null – likely Edge Function crash or empty return');
      throw new Error('Invalid response from server. Please try again.');
    }

    console.log('[apiService.uploadZipFile] Response data:', data);
    return data;
  });
}

export async function uploadGithubRepo(payload: { repoUrl: string }) {
  return await withSupabaseAvailability(async () => {
    console.log('[apiService.uploadGithubRepo] invoked with:', payload);
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      console.error('[apiService.uploadGithubRepo] No active session');
      throw new Error('No active session');
    }
    console.log('[apiService.uploadGithubRepo] session.access_token present:', !!session.access_token);

    // FIXED: Use absolute Supabase Cloud URL instead of relative path
    const functionUrl = getEdgeFunctionUrl('upload-github');
    console.log('[apiService.uploadGithubRepo] Calling Edge Function:', functionUrl);

    const res = await fetch(functionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify(payload),
    });

    console.log('[apiService.uploadGithubRepo] response status:', res.status);
    console.log('[apiService.uploadGithubRepo] response ok:', res.ok);

    if (!res.ok) {
      // Clone the response to avoid 'body already read' error
      const errorText = await res.clone().text();
      console.error('[apiService.uploadGithubRepo] Server error:', errorText);
      throw new Error(`Request failed (${res.status}): ${errorText}`);
    }

    const data = await res.json();
    console.log('[apiService.uploadGithubRepo] Response data:', data);
    return data;
  });
}

export async function scanUrl(payload: { 
  url: string; 
  config?: {
    respectRobots?: boolean;
    maxAssets?: number;
    timeout?: number;
    includeSourceMaps?: boolean;
  }
}) {
  return await withSupabaseAvailability(async () => {
    console.log('[apiService.scanUrl] invoked with:', payload);
    
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error || !session) {
      console.error('[apiService.scanUrl] No active session:', error);
      throw new Error('No session');
    }

    console.log('[apiService.scanUrl] session.access_token present:', !!session.access_token);

    const functionUrl = getEdgeFunctionUrl('scan-url');
    console.log('[apiService.scanUrl] Calling Edge Function:', functionUrl);

    const res = await fetch(functionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify(payload),
    });

    console.log('[apiService.scanUrl] response status:', res.status);
    console.log('[apiService.scanUrl] response ok:', res.ok);

    if (!res.ok) {
      const errorText = await res.clone().text();
      console.error('[apiService.scanUrl] Server error:', errorText);
      throw new Error(`Request failed (${res.status}): ${errorText}`);
    }

    const data = await res.json();
    console.log('[apiService.scanUrl] Response data:', data);
    return data;
  });
}

// Client-side unzip utility for unauthenticated users
export async function unzipFileClientSide(file: File): Promise<{ [filename: string]: string }> {
  console.log('[apiService.unzipFileClientSide] Unzipping file locally:', file.name);
  
  // Import JSZip dynamically
  const JSZip = (await import('jszip')).default;
  
  try {
    const zip = new JSZip();
    const contents = await zip.loadAsync(file);
    const files: { [filename: string]: string } = {};
    
    // Extract all text files
    for (const filename in contents.files) {
      const zipEntry = contents.files[filename];
      if (!zipEntry.dir) {
        try {
          // Only extract text files (common web development files)
          if (filename.match(/\.(js|ts|jsx|tsx|html|css|json|md|txt|env|yml|yaml|xml)$/i)) {
            const content = await zipEntry.async('text');
            files[filename] = content;
            console.log('[apiService.unzipFileClientSide] Extracted:', filename);
          }
        } catch (err) {
          console.warn('[apiService.unzipFileClientSide] Failed to extract:', filename, err);
        }
      }
    }
    
    console.log('[apiService.unzipFileClientSide] Successfully extracted', Object.keys(files).length, 'files');
    return files;
  } catch (err) {
    console.error('[apiService.unzipFileClientSide] Unzip failed:', err);
    throw new Error('Failed to unzip file');
  }
}

// Client-side scanners for unauthenticated users
export function runClientSideScanners(files: { [filename: string]: string }) {
  // Defensive programming - validate input FIRST
  if (!files || typeof files !== 'object') {
    console.error('[apiService.runClientSideScanners] ❌ Invalid files input:', typeof files, files);
    return [];
  }

  const fileKeys = Object.keys(files);
  console.log('[apiService.runClientSideScanners] Running local scanners on', fileKeys.length, 'files');
  
  if (fileKeys.length === 0) {
    console.warn('[apiService.runClientSideScanners] ⚠️ No files to scan');
    return [];
  }
  
  const findings: any[] = [];
  
  // API Key Scanner
  const apiKeyPatterns = [
    { name: 'OpenAI API Key', pattern: /sk-[a-zA-Z0-9]{48}/, severity: 'high' },
    { name: 'Supabase Key', pattern: /sb-[a-zA-Z0-9-_]{100,}/, severity: 'high' },
    { name: 'Stripe Key', pattern: /(sk|pk)_test_[a-zA-Z0-9]{24,}/, severity: 'medium' },
    { name: 'AWS Access Key', pattern: /AKIA[0-9A-Z]{16}/, severity: 'high' },
    { name: 'GitHub Token', pattern: /ghp_[a-zA-Z0-9]{36}/, severity: 'high' },
  ];
  
  for (const [filename, content] of Object.entries(files)) {
    // Defensive check for content
    if (typeof content !== 'string') {
      console.warn('[apiService.runClientSideScanners] ⚠️ Skipping non-string content for:', filename);
      continue;
    }

    try {
      for (const { name, pattern, severity } of apiKeyPatterns) {
        try {
          const matches = content.match(pattern);
          if (matches) {
            const lineNumber = content.substring(0, matches.index || 0).split('\n').length;
            findings.push({
              type: 'api_key_exposure',
              severity,
              title: `${name} Found`,
              description: `Found ${name.toLowerCase()} in ${filename}`,
              file: filename,
              line: lineNumber,
              engine: 'client_regex'
            });
          }
        } catch (patternError) {
          console.warn('[apiService.runClientSideScanners] ⚠️ Pattern matching error for', name, 'in', filename, ':', patternError);
        }
      }
      
      // Hardcoded credentials scanner
      const credentialPatterns = [
        { name: 'Password', pattern: /password\s*[=:]\s*["'][^"']{6,}["']/i, severity: 'medium' },
        { name: 'Secret', pattern: /secret\s*[=:]\s*["'][^"']{8,}["']/i, severity: 'medium' },
        { name: 'Token', pattern: /token\s*[=:]\s*["'][^"']{10,}["']/i, severity: 'medium' },
      ];
      
      for (const { name, pattern, severity } of credentialPatterns) {
        try {
          const matches = content.match(pattern);
          if (matches) {
            const lineNumber = content.substring(0, matches.index || 0).split('\n').length;
            findings.push({
              type: 'hardcoded_credentials',
              severity,
              title: `Hardcoded ${name}`,
              description: `Found hardcoded ${name.toLowerCase()} in ${filename}`,
              file: filename,
              line: lineNumber,
              engine: 'client_regex'
            });
          }
        } catch (patternError) {
          console.warn('[apiService.runClientSideScanners] ⚠️ Pattern matching error for', name, 'in', filename, ':', patternError);
        }
      }
    } catch (fileError) {
      console.error('[apiService.runClientSideScanners] ❌ Error scanning file', filename, ':', fileError);
      // Continue with other files
    }
  }
  
  console.log('[apiService.runClientSideScanners] Found', findings.length, 'issues');
  return findings;
}

// Stripe payment functions
export async function createStripeSession(plan: 'starter' | 'pro' | 'oneoff', options: { token?: string; returnUrl?: string } = {}) {
  return await withSupabaseAvailability(async () => {
    console.log('[apiService.createStripeSession] invoked with plan:', plan, 'returnUrl:', options.returnUrl);
    
    let accessToken = options.token;
    if (!accessToken) {
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error || !session) {
        console.error('[apiService.createStripeSession] No active session:', error);
        throw new Error('Authentication required');
      }
      accessToken = session.access_token;
      console.log('[apiService.createStripeSession] Retrieved access token from current session');
    }

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    };

    const functionUrl = getEdgeFunctionUrl('create-stripe-session');
    console.log('[apiService.createStripeSession] Calling Edge Function:', functionUrl);

    const res = await fetch(functionUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify({ 
        plan, 
        return_url: options.returnUrl || undefined 
      }),
    });

    console.log('[apiService.createStripeSession] response status:', res.status);
    console.log('[apiService.createStripeSession] response ok:', res.ok);

    if (!res.ok) {
      const errorText = await res.clone().text();
      console.error('[apiService.createStripeSession] Server error:', errorText);
      throw new Error(`Request failed (${res.status}): ${errorText}`);
    }

    const data = await res.json();
    console.log('[apiService.createStripeSession] Response data:', data);
    return data;
  });
}

// Check user's subscription status and entitlements
export async function getUserSubscriptionStatus() {
  console.log('[apiService.getUserSubscriptionStatus] Checking subscription status...');
  
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error || !session?.access_token) {
      console.log('[apiService.getUserSubscriptionStatus] No active session');
      return { 
        isProUser: false, 
        hasOneTimeScans: false, 
        subscription: null, 
        entitlements: [],
        credits_remaining: 0,
        isValidProSubscription: false
      };
    }

    // Use the subscription-status Edge Function
    const response = await fetch(getEdgeFunctionUrl('subscription-status'), {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[apiService.getUserSubscriptionStatus] Edge Function error:', response.status, errorText);
      throw new Error(`Failed to fetch subscription status: ${response.status}`);
    }

    const subscriptionData = await response.json();
    console.log('[apiService.getUserSubscriptionStatus] Subscription data:', subscriptionData);

    // Map the new subscription format to the old format for backward compatibility
    const isProUser = subscriptionData.plan === 'pro' && subscriptionData.status === 'active';
    const hasOneTimeScans = subscriptionData.quota_left > 0 && !isProUser;
    const isValidProSubscription = isProUser;

    const result = {
      isProUser,
      hasOneTimeScans,
      subscription: isProUser ? {
        active: true,
        plan: subscriptionData.plan,
        scans_used: subscriptionData.scans_used,
        scan_limit: subscriptionData.scan_limit,
        current_period_end: subscriptionData.period_end,
      } : null,
      entitlements: [], // Legacy field, now empty
      credits_remaining: subscriptionData.quota_left || 0,
      isValidProSubscription
    };

    console.log('[apiService.getUserSubscriptionStatus] Status:', result);
    return result;

  } catch (err) {
    console.error('[apiService.getUserSubscriptionStatus] Error:', err);
    return { 
      isProUser: false, 
      hasOneTimeScans: false, 
      subscription: null, 
      entitlements: [],
      credits_remaining: 0,
      isValidProSubscription: false
    };
  }
}

// Get user's scan history to determine redirect destination after upgrade
export async function getUserScans() {
  console.log('[apiService.getUserScans] Fetching user scan history...');
  
  const { data: { session }, error } = await supabase.auth.getSession();
  if (error || !session) {
    console.log('[apiService.getUserScans] No active session');
    return [];
  }

  try {
    const { data: scans, error: scansError } = await supabase
      .from('scans')
      .select('id, status, created_at, source_type')
      .eq('user_id', session.user.id)
      .order('created_at', { ascending: false });

    if (scansError) {
      console.error('[apiService.getUserScans] Error fetching scans:', scansError);
      return [];
    }

    console.log('[apiService.getUserScans] Found', scans?.length || 0, 'scans for user');
    return scans || [];
  } catch (err) {
    console.error('[apiService.getUserScans] Error:', err);
    return [];
  }
}

// ✅ Debug function to check Stripe session status directly
export const debugStripeSession = async (sessionId: string) => {
  console.log('[apiService.debugStripeSession] Checking Stripe session directly:', sessionId);
  
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      throw new Error('No valid session token');
    }

    const response = await fetch(getEdgeFunctionUrl('debug-stripe-session'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ session_id: sessionId }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[apiService.debugStripeSession] HTTP error:', response.status, errorText);
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const result = await response.json();
    console.log('[apiService.debugStripeSession] Stripe session details:', result);
    return result;
  } catch (error) {
    console.error('[apiService.debugStripeSession] Error:', error);
    throw error;
  }
};

// ✅ Check Stripe session status directly (fallback when webhooks fail)
export const getStripeSessionStatus = async (sessionId: string) => {
  console.log('[apiService.getStripeSessionStatus] Checking Stripe session status:', sessionId);
  
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      throw new Error('No valid session token');
    }

    const response = await fetch(getEdgeFunctionUrl('check-stripe-session'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ session_id: sessionId }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[apiService.getStripeSessionStatus] HTTP error:', response.status, errorText);
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const result = await response.json();
    console.log('[apiService.getStripeSessionStatus] Session status:', result);
    
    // If session is paid but webhook hasn't processed, manually trigger upgrade
    if (result.payment_status === 'paid' && result.metadata?.supabase_user_id) {
      console.log('[apiService.getStripeSessionStatus] 🔍 Session is paid, checking if upgrade was processed...');
      
      // Check if user was already upgraded
      const currentStatus = await getUserSubscriptionStatus();
      if (!currentStatus.isProUser && !currentStatus.hasOneTimeScans) {
        console.log('[apiService.getStripeSessionStatus] ⚠️ Payment completed but user not upgraded - webhook may have failed');
        console.log('[apiService.getStripeSessionStatus] Consider refreshing the page or contacting support if the issue persists');
      }
    }
    
    return result;
  } catch (error) {
    console.error('[apiService.getStripeSessionStatus] Error:', error);
    throw error;
  }
};

// ✅ Manually process a Stripe session when webhook fails (NEW)
export const manuallyProcessStripeSession = async (sessionId: string) => {
  console.log('[apiService.manuallyProcessStripeSession] Manually processing session:', sessionId);
  
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      throw new Error('No valid session token');
    }

    const response = await fetch(getEdgeFunctionUrl('manual-process-stripe-session'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ session_id: sessionId }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[apiService.manuallyProcessStripeSession] HTTP error:', response.status, errorText);
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const result = await response.json();
    console.log('[apiService.manuallyProcessStripeSession] ✅ Session processed manually:', result);
    
    return result;
  } catch (error) {
    console.error('[apiService.manuallyProcessStripeSession] Error:', error);
    throw error;
  }
};

// ✅ Get scan results via API endpoint (replaces direct Supabase queries)
export async function getScanResults(scanId: string) {
  console.log('[apiService.getScanResults] Fetching scan results for:', scanId);
  
  try {
    // Get session and refresh if needed
    let { data: { session }, error: sessionError } = await supabase.auth.getSession();
    console.log('[apiService.getScanResults] Initial session check:', { 
      hasSession: !!session, 
      hasAccessToken: !!session?.access_token,
      userId: session?.user?.id,
      tokenPreview: session?.access_token?.slice(0, 20) + '...',
      sessionError: sessionError?.message
    });
    
    if (sessionError) {
      console.error('[apiService.getScanResults] Session error:', sessionError);
      throw new Error(`Session error: ${sessionError.message}`);
    }
    
    if (!session?.access_token) {
      console.error('[apiService.getScanResults] No session or access token');
      throw new Error('No valid session token');
    }
    
    // Check if token is close to expiration and refresh if needed
    const now = Math.floor(Date.now() / 1000);
    const expiresAt = session.expires_at || 0;
    const timeUntilExpiry = expiresAt - now;
    
    console.log('[apiService.getScanResults] Token expiry check:', {
      now,
      expiresAt,
      timeUntilExpiry,
      expiresInMinutes: Math.floor(timeUntilExpiry / 60)
    });
    
    // If token expires within 5 minutes, refresh it
    if (timeUntilExpiry < 300) {
      console.log('[apiService.getScanResults] Token expiring soon, refreshing...');
      const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
      if (refreshError) {
        console.error('[apiService.getScanResults] Session refresh failed:', refreshError);
        throw new Error('Session refresh failed');
      }
      session = refreshData.session;
      console.log('[apiService.getScanResults] Session refreshed successfully');
      
      if (!session?.access_token) {
        console.error('[apiService.getScanResults] No access token after refresh');
        throw new Error('No valid session token after refresh');
      }
    }

    const url = getEdgeFunctionUrl('get-scan-results');
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
    };
    const body = JSON.stringify({ scan_id: scanId });
    
    console.log('[apiService.getScanResults] Request details:', { 
      url,
      method: 'POST',
      hasAuth: headers.Authorization.startsWith('Bearer '),
      tokenLength: session.access_token.length,
      bodyParsed: JSON.parse(body)
    });

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body,
    });

    console.log('[apiService.getScanResults] Response status:', response.status);
    console.log('[apiService.getScanResults] Response headers:', Object.fromEntries(response.headers.entries()));

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[apiService.getScanResults] HTTP error:', response.status, errorText);
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const result = await response.json();
    console.log('[apiService.getScanResults] Results loaded:', {
      scan_id: result.scan?.id,
      findings_count: result.findings?.length || 0,
      premium_findings: result.premiumFindings || 0,
      is_pro_user: result.isProUser
    });
    
    return result;
  } catch (error) {
    console.error('[apiService.getScanResults] Error:', error);
    throw error;
  }
}; 

// Newsletter subscription function
export async function subscribeToNewsletter(email: string): Promise<{ success: boolean; message: string }> {
  console.log('[apiService.subscribeToNewsletter] Subscribing email to newsletter');
  
  try {
    const url = getEdgeFunctionUrl('subscribe-to-newsletter');
    console.log('[apiService.subscribeToNewsletter] Edge Function URL:', url);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    console.log('[apiService.subscribeToNewsletter] Response status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[apiService.subscribeToNewsletter] HTTP error:', response.status, errorText);
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const result = await response.json();
    console.log('[apiService.subscribeToNewsletter] Subscription result:', result);
    
    return {
      success: result.success || false,
      message: result.message || 'Subscription processed'
    };
  } catch (error) {
    console.error('[apiService.subscribeToNewsletter] Error:', error);
    throw error;
  }
}

export async function submitFeedback(request: SubmitFeedbackRequest): Promise<SubmitFeedbackResponse> {
  const result = await withSupabaseAvailability(async () => {
    console.log('[apiService.submitFeedback] Submitting feedback:', request);
    
    let { data: { session }, error } = await supabase.auth.getSession();
    if (error || !session) {
      console.error('[apiService.submitFeedback] No active session:', error);
      throw new Error('No session');
    }
    
    console.log('[apiService.submitFeedback] session.access_token present:', !!session.access_token);

    const url = getEdgeFunctionUrl('submit-feedback');
    console.log('[apiService.submitFeedback] Edge Function URL:', url);

    const makeRequest = async (token: string) => {
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      };

      try {
        const res = await fetch(url, {
          method: 'POST',
          headers,
          body: JSON.stringify(request),
        });
        return res;
      } catch (networkError) {
        console.error('[apiService.submitFeedback] Network error:', networkError);
        if (isLocalhost()) {
          console.warn('[apiService.submitFeedback] Local development: Edge Function unavailable or blocked by CORS');
        }
        throw networkError;
      }
    };

    // First attempt with current token
    let res = await makeRequest(session.access_token);

    // If we get a 401, try refreshing the session once
    if (res.status === 401) {
      console.log('[apiService.submitFeedback] Got 401, attempting to refresh session...');
      
      const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
      if (refreshError || !refreshData.session?.access_token) {
        console.error('[apiService.submitFeedback] Session refresh failed:', refreshError);
        throw new Error('Authentication required');
      }
      
      console.log('[apiService.submitFeedback] Session refreshed, retrying request...');
      res = await makeRequest(refreshData.session.access_token);
    }

    console.log('[apiService.submitFeedback] Response status:', res.status);

    if (!res.ok) {
      const errorText = await res.clone().text();
      console.error('[apiService.submitFeedback] Server error:', errorText);
      throw new Error(`Request failed (${res.status}): ${errorText}`);
    }

    const data = await res.json();
    console.log('[apiService.submitFeedback] Feedback submitted successfully:', data);
    return data as SubmitFeedbackResponse;
  });

  if (result === null) {
    throw new Error('Supabase is currently unavailable. Please try again later.');
  }

  return result;
}

export async function getDemoScanResults() {
  // Fetch the single demo row from public_demo_scan_results (public, no auth required)
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error('VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY not configured');
  }
  const { createClient } = await import('@supabase/supabase-js');
  const publicClient = createClient(supabaseUrl, supabaseAnonKey);
  const { data, error } = await publicClient
    .from('public_demo_scan_results')
    .select('findings, metadata')
    .eq('scan_id', 'demo')
    .single();
  if (error) {
    console.error('[apiService.getDemoScanResults] Error fetching demo results:', error);
    throw new Error('Failed to fetch demo scan results');
  }
  return {
    scan: { id: 'demo', metadata: data.metadata },
    findings: data.findings || [],
    isDemo: true
  };
}